import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Search, Music, Loader2, Plus, Play, Pause, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { motion, AnimatePresence } from "framer-motion";
import { useAudioPreloader } from "@/hooks/useAudioPreloader";
interface Track {
  id: string;
  name: string;
  artist: string;
  album: string;
  imageUrl: string;
  previewUrl: string | null;
  spotifyUrl: string;
  uri: string;
  durationMs?: number;
}
interface SpotifySearchProps {
  onAddSong: (song: Track) => void;
  shouldPulse?: boolean;
  playingSongId?: string | null;
  onPlayToggle?: (track: Track) => void;
  audioConsented?: boolean;
  disabled?: boolean;
}
export const SpotifySearch = ({
  onAddSong,
  shouldPulse = false,
  playingSongId = null,
  onPlayToggle,
  audioConsented = true,
  disabled = false
}: SpotifySearchProps) => {
  const {
    toast
  } = useToast();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Track[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [celebratePosition, setCelebratePosition] = useState<{ x: number; y: number } | null>(null);
  
  // Preload audio for search results for instant playback
  useAudioPreloader(results, 8);

  // Auto-search as user types (debounced)
  useEffect(() => {
    if (!query.trim() || !isOpen) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    
    const debounceTimer = setTimeout(async () => {
      console.log('🔍 [SpotifySearch] Searching Spotify for:', query.trim());
      try {
        const {
          data,
          error
        } = await supabase.functions.invoke('spotify-search', {
          body: {
            query: query.trim(),
            limit: 8
          }
        });
        
        console.log('🔌 [SpotifySearch] invoke response:', { data, error });
        
        if (error) {
          console.error('❌ [SpotifySearch] invoke error object:', error);
          console.error('❌ [SpotifySearch] invoke error JSON:', JSON.stringify(error, null, 2));
          throw error;
        }
        
        console.log('✅ [SpotifySearch] search results:', data?.tracks?.length || 0, 'tracks');
        setResults(data.tracks || []);
      } catch (err: unknown) {
        console.error('❌ [SpotifySearch] Search failed:', err);
        setResults([]);
        
        const message = err instanceof Error 
          ? err.message 
          : typeof err === 'string' 
          ? err 
          : 'Unknown error while calling Spotify search';
        
        toast({
          title: "Spotify Search Failed",
          description: message,
          variant: "destructive"
        });
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce for faster results

    return () => {
      clearTimeout(debounceTimer);
      setIsSearching(false);
    };
  }, [query, isOpen, toast]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setIsSearching(true);
    console.log('🔍 [SpotifySearch] Manual search for:', query.trim());
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('spotify-search', {
        body: {
          query: query.trim(),
          limit: 8
        }
      });
      
      console.log('🔌 [SpotifySearch] Manual search invoke response:', { data, error });
      
      if (error) {
        console.error('❌ [SpotifySearch] Manual search invoke error object:', error);
        console.error('❌ [SpotifySearch] Manual search invoke error JSON:', JSON.stringify(error, null, 2));
        throw error;
      }
      
      console.log('✅ [SpotifySearch] Manual search results:', data?.tracks?.length || 0, 'tracks');
      setResults(data.tracks || []);
      if (data.tracks.length === 0) {
        toast({
          title: "No results",
          description: "Try a different search term"
        });
      }
    } catch (err: unknown) {
      console.error('❌ [SpotifySearch] Manual search error:', err);
      
      const message = err instanceof Error 
        ? err.message 
        : typeof err === 'string' 
        ? err 
        : 'Unknown error while searching Spotify';
      
      toast({
        title: "Spotify Search Failed",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };
  const handleAddSong = (track: Track, event: React.MouseEvent) => {
    // Trigger celebration at click position
    const rect = event.currentTarget.getBoundingClientRect();
    setCelebratePosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    
    onAddSong(track);
    
    // Clear celebration after animation
    setTimeout(() => {
      setCelebratePosition(null);
      setIsOpen(false);
      setQuery("");
      setResults([]);
    }, 600);
  };
  return <>
    {/* Celebration particles */}
    <AnimatePresence>
      {celebratePosition && (
        <div className="fixed inset-0 pointer-events-none z-[100]" style={{ left: celebratePosition.x, top: celebratePosition.y }}>
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ 
                x: 0, 
                y: 0, 
                scale: 0,
                opacity: 1 
              }}
              animate={{ 
                x: Math.cos((i * Math.PI * 2) / 12) * (80 + Math.random() * 40),
                y: Math.sin((i * Math.PI * 2) / 12) * (80 + Math.random() * 40),
                scale: [0, 1.2, 0],
                opacity: [1, 1, 0]
              }}
              transition={{ 
                duration: 0.6,
                ease: "easeOut"
              }}
              className="absolute"
            >
              <Sparkles 
                className="h-4 w-4" 
                style={{ 
                  color: `hsl(${(i * 30) % 360}, 70%, 60%)`,
                  filter: 'drop-shadow(0 0 4px currentColor)'
                }} 
              />
            </motion.div>
          ))}
          
          {/* Central pulse */}
          <motion.div
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: 3, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-accent/30"
          />
        </div>
      )}
    </AnimatePresence>
    
    <Popover open={isOpen && !disabled} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          size="lg"
          disabled={disabled}
          className={`gap-2 border-2 border-accent/50 hover:bg-accent/10 hover:border-accent transition-all ${
            shouldPulse && !disabled ? 'animate-pulse shadow-lg shadow-accent/50 border-accent' : ''
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Search className="h-4 w-4" />
          Search Spotify
          {shouldPulse && !disabled && <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-accent"></span>
          </span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end">
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-border">
            <Music className="h-5 w-5 text-accent" />
            <div>
              <h3 className="font-semibold text-sm">Add Songs for Setlist</h3>
              <p className="text-xs text-muted-foreground">Help us curate your perfect musical experience</p>
            </div>
          </div>
          <div className="relative">
            <Input 
              placeholder="Search for songs or artists..." 
              value={query} 
              onChange={e => setQuery(e.target.value)} 
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              className="pr-10"
            />
            {isSearching && (
              <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            )}
          </div>

          {results.length > 0 && <div className="max-h-96 overflow-y-auto space-y-2">
              {results.map(track => <Card key={track.id} className="p-3 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    {track.imageUrl && <img src={track.imageUrl} alt={track.name} className="w-12 h-12 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{track.name}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {track.artist}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {onPlayToggle && (
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPlayToggle(track);
                          }}
                          className="h-8 w-8"
                        >
                          {playingSongId === track.id ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      <Button 
                        size="icon" 
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSong(track, e);
                        }}
                        className="relative overflow-hidden hover:bg-accent/20 hover:text-accent transition-all hover:scale-110"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>)}
            </div>}
        </div>
      </PopoverContent>
    </Popover>
  </>;
};